import {
	FileWithMetadataPayload,
	StartClassificationAndExtractionResponse,
} from "@dune/types";
import { Loader } from "@dune/ui";
import { Eye, Trash03 } from "@untitled-ui/icons-react";
import { useDocumentStore } from "../../stores/document.store";
import { CompactProcessingJobsDetails } from "./ProcessingJobDetails";

interface FileWithAiJobCardProps {
	id: string;
	file?: FileWithMetadataPayload;
	isLoading?: boolean;
	isFetching?: boolean;
	onRemove: (id: string) => void;
	startAiJobResponseError?: StartClassificationAndExtractionResponse["message"]["files_failed"][number]["error_message"];
	readonly?: boolean;
}

export default function FileWithAiJobCard({
	id,
	file,
	isLoading = false,
	isFetching = false,
	onRemove,
	startAiJobResponseError,
	readonly = false,
}: FileWithAiJobCardProps) {
	const fileId = useDocumentStore((state) => state.fileId);
	const setFileId = useDocumentStore((state) => state.setFileId);

	const handleView = async () => {
		if (fileId === id) {
			setFileId(null);
			return;
		}
		setFileId(id);
	};
	if (isLoading) {
		return (
			<div className="flex items-center border rounded-lg p-4 bg-gray-200 shadow-sm animate-pulse h-[120px]" />
		);
	}

	if (!file) {
		return (
			<div className="flex items-center border rounded-lg p-4 bg-white shadow-sm h-[120px]">
				<span className="text-gray-500">Fichier non trouvé</span>
			</div>
		);
	}

	const processingJobs = file.processingJobs || [];

	return (
		<div className="border rounded-lg p-4 bg-white shadow-sm">
			<div className="flex items-center justify-between mb-3">
				<div className="flex flex-col">
					<span className="font-medium text-gray-900">{file.name}</span>
					{file.size && (
						<span className="text-xs text-gray-500">
							{formatSize(file.size)}
						</span>
					)}
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						className="p-2 rounded hover:bg-gray-100 transition"
						onClick={handleView}
					>
						<Eye className="w-5 h-5 text-blue-500" />
					</button>
					{!readonly && (
						<button
							type="button"
							className="p-2 rounded hover:bg-gray-100 transition"
							onClick={() => onRemove(id)}
						>
							<Trash03 className="w-5 h-5 text-red-500" />
						</button>
					)}
				</div>
			</div>

			<div className="flex gap-2">
				{startAiJobResponseError && (
					<div className="text-xs text-red-500 italic">
						{startAiJobResponseError}
					</div>
				)}
				{processingJobs.length === 0 ? (
					<div className="text-xs text-gray-500 italic">
						Aucun traitement démarré
					</div>
				) : (
					<CompactProcessingJobsDetails jobs={processingJobs} />
				)}
				{isFetching && <Loader size={14} color="gray" />}
			</div>
		</div>
	);
}

function formatSize(size?: number) {
	if (size === undefined || size === null) return "?";
	if (size < 1024) return `${size} o`;
	if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} Ko`;
	return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
}
